import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Render the normalised CIP export (pipelines.api `GET /onboarding-cip/:id`) as a pretty,
 * print-ready PDF — one section per verification stage. Pure client-side (jsPDF + autotable), no
 * backend document endpoint required. Triggers a browser download.
 */

const BRAND = { r: 91, g: 79, b: 214 }; // simplici purple, matches the modal header accent

const fmt = (v: any): string => {
	if (v == null || v === '') return '--';
	if (Array.isArray(v)) return v.length ? v.map(fmt).join(', ') : '--';
	if (typeof v === 'boolean') return v ? 'Yes' : 'No';
	// ISO date/time → locale string; leave other strings/numbers as-is.
	if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
		const d = new Date(v);
		return isNaN(d.getTime()) ? v : d.toLocaleString();
	}
	if (typeof v === 'object') {
		const parts = Object.values(v).map(fmt).filter(x => x !== '--');
		return parts.length ? parts.join(', ') : '--';
	}
	return String(v);
};

const addr = (a: any): string => {
	if (!a) return '--';
	return (
		[a.line1, a.line2, a.city, a.state, a.postalCode, a.country]
			.filter(Boolean)
			.join(', ') || '--'
	);
};

export const buildCipReport = (cip: any): void => {
	if (!cip) return;
	const doc = new jsPDF({ unit: 'pt', format: 'a4' });
	const pageW = doc.internal.pageSize.getWidth();
	const marginX = 40;
	let y = 48;

	// ---- title block --------------------------------------------------------
	doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
	doc.rect(0, 0, pageW, 6, 'F');
	doc.setFont('helvetica', 'bold');
	doc.setFontSize(18);
	doc.setTextColor(20, 20, 20);
	doc.text('Customer Identification Program (CIP)', marginX, y);
	y += 22;
	doc.setFont('helvetica', 'normal');
	doc.setFontSize(11);
	doc.setTextColor(90, 90, 90);
	const who = cip.applicant?.fullName || cip.applicant?.email || cip.onboardingSessionId || '';
	doc.text(`${who}`, marginX, y);
	y += 15;
	doc.setFontSize(9);
	doc.text(`Generated ${new Date().toLocaleString()}`, marginX, y);
	y += 10;

	const section = (title: string, rows: Array<[string, any]>) => {
		const body = rows.map(([k, v]) => [k, fmt(v)]);
		autoTable(doc, {
			startY: y + 14,
			head: [[title, '']],
			body,
			theme: 'grid',
			margin: { left: marginX, right: marginX },
			styles: { fontSize: 9, cellPadding: 5, textColor: [40, 40, 40], lineColor: [230, 230, 230] },
			headStyles: {
				fillColor: [BRAND.r, BRAND.g, BRAND.b],
				textColor: [255, 255, 255],
				fontStyle: 'bold',
				fontSize: 10,
			},
			columnStyles: { 0: { cellWidth: 170, fontStyle: 'bold', textColor: [90, 90, 90] } },
		});
		y = (doc as any).lastAutoTable.finalY;
	};

	// ---- overview -----------------------------------------------------------
	section('Overview', [
		['Onboarding session', cip.onboardingSessionId],
		['Tier-1 session', cip.sessionId],
		['Business', cip.businessId],
		['Status', cip.status],
		['Tier', cip.tier ? `${cip.tier.current}${cip.tier.completed ? ' (completed)' : ''}` : '--'],
		['Completed at', cip.completedAt],
		['Providers', cip.providerNames],
	]);

	// ---- applicant identity -------------------------------------------------
	const a = cip.applicant ?? {};
	section('Applicant identity', [
		['Full name', a.fullName],
		['First name', a.firstName],
		['Last name', a.lastName],
		['Date of birth', a.dateOfBirth],
		['Nationality', a.nationality],
		['Email', a.email],
		['Phone', a.phone ? `${a.countryCode ?? ''} ${a.phone}`.trim() : '--'],
		['Address', addr(a.address)],
		['ID type', a.idType],
		['ID number', a.idNumber],
		['ID issuing state', a.idIssuingState],
		['ID expiry', a.idExpiry],
		['SSN verified', a.ssnVerified],
	]);

	// ---- KYC ----------------------------------------------------------------
	if (cip.kyc) {
		const k = cip.kyc;
		const vf = k.verified ?? {};
		/* NO VENDOR NAME ON THE REPORT. Which vendor produced the check is our
		   procurement detail; naming it on a compliance record invites the reader
		   to weigh the finding by the brand behind it, and it dates the document —
		   the vendor changes and the archived report still names the old one. */
		section('KYC', [
			['Status', k.status],
			['Check ID', k.checkId],
			['Match type', k.matchType],
			['Face match score', k.faceMatchScore],
			['SSN required', k.ssnRequired],
			['SSN verified', k.ssnVerified],
			['Verified: name', vf.name],
			['Verified: date of birth', vf.dateOfBirth],
			['Verified: address', vf.address],
			['Verified: ID number', vf.idNumber],
			['Verified: nationality', vf.nationality],
			['Completed at', k.completedAt],
		]);
	}

	// ---- KYB (only when present) --------------------------------------------
	if (cip.kyb) {
		section('KYB', [
			['Status', cip.kyb.status],
			['Check ID', cip.kyb.checkId],
			['Completed at', cip.kyb.completedAt],
		]);
	}

	// ---- watchlist / AML ----------------------------------------------------
	if (cip.watchlist) {
		const w = cip.watchlist;
		section('Watchlist / AML', [
			['Status', w.status],
			['Providers', w.providers],
			['Lists screened', w.lists],
			['Match count', w.matchCount],
			['Screens', (w.screens ?? []).map((s: any) => `${s.provider}:${s.status}`)],
			['Completed at', w.completedAt],
		]);
	}

	// ---- questionnaire ------------------------------------------------------
	(cip.questionnaire ?? []).forEach((q: any, i: number) => {
		const answers = q.answers ?? {};
		const rows: Array<[string, any]> = Object.keys(answers).length
			? Object.entries(answers).map(([k, v]) => [k, v] as [string, any])
			: [['(no answers captured)', '']];
		section(`Questionnaire ${cip.questionnaire.length > 1 ? i + 1 : ''}${q.label ? ` — ${q.label}` : ''}`.trim(), rows);
	});

	// ---- document review ----------------------------------------------------
	if ((cip.documentReview ?? []).length) {
		section(
			'Document review',
			cip.documentReview.map((d: any) => [d.docId, d.acknowledged ? 'Acknowledged' : 'Not acknowledged'])
		);
	}

	// ---- consent & audit ----------------------------------------------------
	const c = cip.consent ?? {};
	const au = cip.audit ?? {};
	section('Consent & audit', [
		['Consent accepted', c.accepted],
		['Consent version', c.version],
		['Consent scope', c.scope],
		['Consent timestamp', c.timestamp ? new Date(c.timestamp).toLocaleString() : '--'],
		['IP address', au.ipAddress],
		['Observed IP', au.observedIp],
		['Created at', au.createdAt],
		['Updated at', au.updatedAt],
	]);

	doc.save(`cip-report-${cip.sessionId ?? cip.onboardingSessionId ?? 'session'}.pdf`);
};
